import { Component } from '@theme/component';

/**
 * @typedef {Object} PtaFinderRefs
 * @property {HTMLSelectElement} from - Platform the shopper owns
 * @property {HTMLSelectElement} to - Platform they want to power, narrowed to real matches
 * @property {HTMLElement} labelFrom - Heading above the first select
 * @property {HTMLElement} labelTo - Heading above the second select
 * @property {HTMLButtonElement} flip - Swaps which side is asked for first
 * @property {HTMLAnchorElement} [ask] - "Can't find it" link into the contact form
 */

/** Battery-first is the default: it is how the store's own product titles read. */
const BATTERY_FIRST = 'battery';
const TOOL_FIRST = 'tool';

/**
 * The Battery Adapter Finder.
 *
 * Two dependent selects: pick what you own, and the second list narrows to the platforms an
 * adapter actually exists for. Choosing one goes straight to that product, because the
 * matrix is exactly 1:1 — 377 live adapters, 377 distinct pairs.
 *
 * The matrix is a JSON island written by `tools/nightly/build-finder-data.mjs` from
 * `version2.battery_brand` / `version2.tool_brand`, so no brand name or product URL is
 * hand-maintained here. Every option in both lists is derived from a product that exists,
 * which is why there is no "we don't carry that" dead end left to hit — unlike the Debut
 * version, whose brand list was static and could offer combinations it had nothing for.
 *
 * Deep-linkable, so a collection or article can drop someone in mid-flow:
 *
 *   /pages/battery-adapters-finder?battery=Makita%2018V%20LXT
 *   /pages/battery-adapters-finder?tool=DeWalt%2020V%20MAX
 *
 * @extends {Component<PtaFinderRefs>}
 */
class PtaFinder extends Component {
  /** @type {{platforms: string[], platformHandles: string[], pairs: [number, number, string][]}} */
  #data = { platforms: [], platformHandles: [], pairs: [] };

  /** batteryIndex -> Map(toolIndex -> handle) */
  #forward = new Map();

  /** toolIndex -> Map(batteryIndex -> handle) */
  #reverse = new Map();

  #direction = BATTERY_FIRST;

  async connectedCallback() {
    super.connectedCallback();

    if (!(await this.#load())) return;

    for (const select of [this.refs.from, this.refs.to].filter(Boolean)) select.disabled = false;

    for (const [battery, tool, handle] of this.#data.pairs) {
      if (!this.#forward.has(battery)) this.#forward.set(battery, new Map());
      if (!this.#reverse.has(tool)) this.#reverse.set(tool, new Map());

      this.#forward.get(battery)?.set(tool, handle);
      this.#reverse.get(tool)?.set(battery, handle);
    }

    this.refs.flip?.addEventListener('click', () => this.#flip());
    this.refs.from?.addEventListener('change', () => this.#fillSecond());
    this.refs.to?.addEventListener('change', () => this.#go());

    this.#render();
    this.#applyUrlParams();
  }

  /**
   * The matrix arrives as an ES module from `assets/`, chosen by locale in Liquid, rather than as a
   * `<script>` island inlined into every page that carries a finder. It is fetched once and then served
   * from cache, so the second and later pages cost nothing.
   *
   * The cost is that it is **asynchronous**, so the selects are disabled and labelled with a translated
   * "loading" string until it lands. Disabled rather than merely empty, because an enabled select a
   * shopper can open and find empty reads as broken, where a disabled one reads as not-ready.
   */
  async #load() {
    const src = this.dataset.src;
    if (!src) return false;

    for (const select of [this.refs.from, this.refs.to, this.refs.other].filter(Boolean)) {
      select.disabled = true;
      select.replaceChildren(new Option(this.#text('loading'), ''));
    }

    try {
      const mod = await import(src);
      this.#data = mod.default ?? mod;
      return Array.isArray(this.#data?.pairs);
    } catch {
      /* Left disabled and labelled. A finder that cannot load its data should not offer choices it
         cannot act on, and the visible label already says what happened. */
      return false;
    }
  }

  /** The lookup that leads, given the current direction. */
  get #primary() {
    return this.#direction === BATTERY_FIRST ? this.#forward : this.#reverse;
  }

  #text(key) {
    return this.dataset[key] ?? '';
  }

  /**
   * Rebuild both selects and the two headings for the current direction.
   *
   * **No `aria-label` on either select, deliberately.** Both are named by a real `<label for>` in
   * `blocks/pta-finder.liquid`, and the two lines that used to set one here were removed with it.
   * An `aria-label` overrides a `<label>`, so leaving them would have put the accessible name back
   * to "Select a Brand of Battery" while the visible label read "Your battery" — WCAG 2.5.3, and
   * the reason the label exists.
   *
   * Rewriting `labelFrom`/`labelTo` below is what keeps the name correct across a flip: the
   * accessible name now follows the visible text rather than being maintained beside it.
   */
  #render() {
    const { from, to, labelFrom, labelTo } = this.refs;
    const batteryFirst = this.#direction === BATTERY_FIRST;

    if (labelFrom) labelFrom.textContent = this.#text(batteryFirst ? 'labelBattery' : 'labelTool');
    if (labelTo) labelTo.textContent = this.#text(batteryFirst ? 'labelOnTool' : 'labelOnBattery');

    if (from) {
      this.#fill(from, [...this.#primary.keys()], this.#text(batteryFirst ? 'selectBattery' : 'selectTool'));
    }

    if (to) {
      this.#fill(to, [], this.#text(batteryFirst ? 'selectTool' : 'selectBattery'));
      to.disabled = true;
    }

    this.#syncAsk();
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {number[]} indexes
   * @param {string} placeholder
   */
  /**
   * Built as DOM nodes, not as an `innerHTML` string.
   *
   * `new Option(text, value)` assigns the label as text, so a platform name is never parsed as markup.
   * The previous version interpolated `name` into `<option value="${index}">${name}</option>` and set
   * `innerHTML`, which made a metaobject field an injection point into this page.
   *
   * The generator used to carry a `<` escape that claimed to cover this. It could not, for two
   * reasons: it was written with one backslash, so it replaced `<` with `<` and did nothing at all; and
   * once the finder data became an ES module rather than a `<script type="application/json">` island,
   * even the correct two-backslash form would have been decoded by the JS parser before the string
   * existed. Verified by importing both forms and comparing. Escaping at the producer cannot help a
   * consumer that concatenates markup — so the fix belongs here, and the escape is gone.
   */
  #fill(select, indexes, placeholder) {
    // Sorted by the label the shopper reads, not by the index, which follows the full list.
    const sorted = indexes
      .map((index) => ({ index, name: this.#data.platforms[index] }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    select.replaceChildren(
      new Option(placeholder, ''),
      ...sorted.map(({ index, name }) => new Option(name, index))
    );
  }

  /** Narrow the second list to the platforms the first one actually pairs with. */
  #fillSecond() {
    const { from, to } = this.refs;

    if (!from || !to) return;

    const matches = this.#primary.get(Number(from.value));
    const batteryFirst = this.#direction === BATTERY_FIRST;

    this.#fill(
      to,
      matches ? [...matches.keys()] : [],
      this.#text(batteryFirst ? 'selectTool' : 'selectBattery')
    );

    to.disabled = !matches;
    this.#syncAsk();
    this.#reportFirstChoice(from.value, matches ? matches.size : 0);
  }

  /**
   * Report the first select being chosen, which is the step the funnel was blind to.
   *
   * `finder_click` only fires from `#go`, and `#go` runs off the *second* select's change — so
   * every recorded finder journey is a completed one. A shopper who picks their battery and
   * then stops has always been invisible, which means the one number nobody could compute was
   * the drop-off. First-choices minus `finder_click`s is that number.
   *
   * `matches` is carried because zero of them is a different event in disguise: it means this
   * platform pairs with nothing in the catalogue, so the shopper is about to hit a dead end
   * that is a stocking decision rather than a UX one.
   *
   * Published as `pta_interaction` like everything in `assets/pta-track.js`, not as a name of
   * its own — see that file for why. Without the "PTA Custom Events" pixel subscribing, this
   * is a no-op, exactly as `finder_click` above is.
   *
   * The surface is a constant because this file only ever powers `<pta-finder>`, which is the
   * home page and the battery-finder page. The collection pages carry `<pta-collection-finder>`,
   * a **separate component in its own file**, so it is not covered here — and home versus
   * finder page separates itself on GA4's own `page_location` without a param for it.
   *
   * @param {string} value - The chosen platform's index into `#data.platforms`
   * @param {number} matches - How many platforms it pairs with
   */
  #reportFirstChoice(value, matches) {
    if (value === '') return;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    window.Shopify.analytics.publish('pta_interaction', {
      action: 'finder_select',
      surface: 'finder',
      label: this.#data.platforms[Number(value)],
      direction: this.#direction,
      matches: String(matches),
    });
  }

  /** Go to the one adapter that joins the two selected platforms. */
  #go() {
    const { from, to } = this.refs;
    const handle = this.#primary.get(Number(from?.value))?.get(Number(to?.value));

    if (!handle) return;

    const root = this.dataset.rootUrl === '/' ? '' : (this.dataset.rootUrl ?? '');
    const url = `${root}/products/${handle}`;

    /* Published into Shopify's web-pixels sandbox rather than called on `window.gtag`, which does not exist
       on this storefront. Analytics reaches the store through the Google & YouTube channel, which Shopify
       runs as an app pixel inside an iframe — the measurement ID appears on the page only inside that pixel's
       configuration, and the string `gtag` appears exactly once in a product page, as a key in that JSON.

       **This event worked for about five years and then stopped, and the stopping is invisible.** Debut
       carried a theme-level tag, so `window.gtag` was a real function and the guard passed; there is finder
       data going back that far to prove it. Moving analytics into the app pixel took the function away
       without touching this file, so the guard silently began failing and the redirect below simply ran
       without its 200 ms grace. No error, no missing page, nothing to notice — only a metric that flattened.

       Which is the durable lesson rather than the fix: theme code cannot reach anything inside the pixel
       sandbox, and an analytics call that has always worked is not evidence that it still does.

       A custom pixel in the admin — "PTA Custom Events" — subscribes to the name below and forwards it on.
       Nothing in this repository can verify that half, so if the event stops arriving, check there first. */
    if (typeof window.Shopify?.analytics?.publish === 'function') {
      window.Shopify.analytics.publish('finder_click', {
        event_category: 'Battery Finder',
        event_label: 'Finder Redirect',
        direction: this.#direction,
        battery_brand: this.#data.platforms[Number(this.#direction === BATTERY_FIRST ? from?.value : to?.value)],
        tool_brand: this.#data.platforms[Number(this.#direction === BATTERY_FIRST ? to?.value : from?.value)],
      });

      // Debut delayed the hop so the event had time to leave. Keeping that.
      setTimeout(() => { window.location.href = url; }, 200);
      return;
    }

    window.location.href = url;
  }

  #flip() {
    this.#direction = this.#direction === BATTERY_FIRST ? TOOL_FIRST : BATTERY_FIRST;
    this.dataset.direction = this.#direction;
    this.#render();
    this.refs.from?.focus();
  }

  /**
   * Carry the current selection into the contact form, which reads the same platform names
   * in its own battery/tool selects — so the enquiry arrives already half answered.
   */
  #syncAsk() {
    const { ask, from } = this.refs;

    if (!ask) return;

    const base = ask.dataset.baseHref ?? ask.getAttribute('href') ?? '';
    const name = this.#data.platforms[Number(from?.value)];

    ask.dataset.baseHref = base;

    if (!from?.value || !name) {
      ask.setAttribute('href', base);
      return;
    }

    const side = this.#direction === BATTERY_FIRST ? 'battery' : 'tool';

    ask.setAttribute('href', `${base}&${side}=${encodeURIComponent(name)}`);
  }

  /** `?battery=` / `?tool=` preselect, and set the direction to match. */
  #applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const battery = params.get('battery');
    const tool = params.get('tool');
    const wanted = battery ?? tool;

    if (!wanted) return;

    if (!battery && tool) this.#flip();

    const index = this.#data.platforms.indexOf(wanted);

    if (index < 0 || !this.refs.from) return;

    const match = [...this.refs.from.options].find((option) => option.value === String(index));

    if (!match) return;

    this.refs.from.value = String(index);
    this.#fillSecond();
  }
}

customElements.define('pta-finder', PtaFinder);
